import { useCallback, useMemo, useRef, useState } from "react";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import type { MeasurementOverlayRecord } from "@/viewport-tools/measurement/overlay";
import { useUndoRedoHistory } from "@/hooks/use-undo-redo-history";
import { notifyError } from "@/lib/notify-error";
import { AiHistoryLineage } from "./ai-history-lineage";
import { MeasurementHistoryLineage } from "./measurement-history-lineage";

/** Owns history while the job page remains mounted, independent of its viewer. */
export function useTakeoffJobHistory(jobId: Id<"takeoffPilotJobs"> | undefined, enabled: boolean) {
  const [, setRevision] = useState(0);
  const queues = useRef(new Map<string, { tail: Promise<void>; pending: number }>());
  const queue = useMemo(() => {
    const key = String(jobId);
    let existing = queues.current.get(key);
    if (!existing) { existing = { tail: Promise.resolve(), pending: 0 }; queues.current.set(key, existing); }
    return existing;
  }, [jobId]);
  const pendingCount = queue.pending;
  const canApply = useCallback(() => queue.pending === 0, [queue]);
  const history = useUndoRedoHistory(String(jobId), 50, (error) => {
    console.error(error);
    notifyError(error, "Failed to update edit history");
  }, enabled && pendingCount === 0, canApply);
  const aiHistory = useMemo(() => new AiHistoryLineage(), [jobId]);
  const measurementHistory = useMemo(() => new MeasurementHistoryLineage(), [jobId]);
  const aiReviewCursors = useMemo(() => new Map<Id<"takeoffPilotAiReviewActions">, { proposals: ReturnType<AiHistoryLineage["track"]>; measurements: ReturnType<MeasurementHistoryLineage["track"]> }>(), [jobId]);
  const measurementEditQueues = useMemo(() => new Map<string, Promise<void>>(), [jobId]);
  const latestMeasurementState = useMemo(() => new Map<string, MeasurementOverlayRecord>(), [jobId]);
  const enqueue = useCallback(<T,>(operation: () => Promise<T>) => {
    queue.pending += 1;
    setRevision(revision => revision + 1);
    const result = queue.tail.then(operation);
    queue.tail = result.then(() => undefined, () => undefined);
    void queue.tail.finally(() => {
      queue.pending -= 1;
      setRevision(revision => revision + 1);
    });
    return result;
  }, [queue]);
  return { history, aiHistory, measurementHistory, aiReviewCursors, measurementEditQueues, latestMeasurementState, enqueue, pendingCount };
}

export type TakeoffJobHistory = ReturnType<typeof useTakeoffJobHistory>;
